import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';
import { generateWebPageSchema, generateOrganizationSchema, generateBreadcrumbSchema } from '../../utils/seo';

const AboutPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // Generate structured data
  const structuredData = [
    generateWebPageSchema(
      '会社概要｜おかやま放課後等デイサービス｜岡山市北区の発達支援',
      '岡山市北区の放課後等デイサービス「おかやま放課後等デイサービス」の会社概要。2000年設立以来、発達支援に携わり、多くのお子様とご家族の成長をサポート。',
      '/about'
    ),
    generateOrganizationSchema(),
    generateBreadcrumbSchema([
      { name: 'ホーム', url: '/' },
      { name: '会社概要', url: '/about' }
    ])
  ];

  const companyInfo = {
    name: 'おかやま放課後等デイサービス',
    representative: '岡山 太郎',
    established: '2000年',
    employees: '5名',
    address: '〒700-0000 岡山市北区○○町1-2-3',
    phone: '086-012-3456',
    fax: '086-012-3457',
    hours: '9:00 〜 18:00',
    closed: '日曜日・祝日'
  };

  const certifications = [
    '放課後等デイサービス事業指定',
    '児童発達支援事業指定',
    '岡山市指定障害児通所支援事業者',
    '全国放課後等デイサービス連絡協議会加盟',
    '岡山県社会福祉協議会会員'
  ];

  const philosophy = [
    {
      title: '一人ひとりを大切に',
      description: 'お子様の個性と可能性を尊重し、その子らしい成長を支援します。',
      icon: 'ri-heart-fill',
      color: 'text-red-500'
    },
    {
      title: '専門性の追求',
      description: '常に最新の知識と技術を学び、質の高い療育を提供します。',
      icon: 'ri-book-fill',
      color: 'text-blue-500'
    },
    {
      title: '家族との連携',
      description: 'ご家族と密に連携し、一貫した支援を行います。',
      icon: 'ri-team-fill',
      color: 'text-green-500'
    },
    {
      title: '地域との共生',
      description: '地域社会の一員として、共に歩んでいきます。',
      icon: 'ri-community-fill',
      color: 'text-purple-500'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="会社概要｜おかやま放課後等デイサービス｜岡山市北区の発達支援"
        description="岡山市北区の放課後等デイサービス「おかやま放課後等デイサービス」の会社概要。2000年設立以来、発達支援に携わり、多くのお子様とご家族の成長をサポートしてまいりました。"
        keywords="岡山市,放課後等デイサービス,会社概要,発達支援,療育,企業情報"
        canonicalUrl={`${siteUrl}/about`}
        structuredData={structuredData}
      />
      
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 md:py-24 bg-gradient-to-br from-green-600 to-blue-600">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 md:mb-8">
            <strong>会社概要</strong>
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed">
            2000年の設立以来、岡山市の発達支援に携わり、多くのお子様とご家族の成長をサポートしてまいりました。
          </p>
        </div>
      </section>

      {/* Company Philosophy */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>私たちの理念</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              お子様一人ひとりの可能性を信じ、その子らしい成長を全力でサポートします。
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {philosophy.map((item, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 md:p-8 text-center hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
                <div className={`w-16 h-16 md:w-20 md:h-20 ${item.color} bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6`}>
                  <i className={`${item.icon} text-2xl md:text-3xl ${item.color}`}></i>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4 break-words">{item.title}</h3>
                <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed break-words">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Information */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>会社情報</strong>
            </h2>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl shadow-xl p-8 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              <div className="space-y-6 md:space-y-8">
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">会社名</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600 break-words">{companyInfo.name}</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">代表取締役</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600">{companyInfo.representative}</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">設立</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600">{companyInfo.established}</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">従業員数</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600">{companyInfo.employees}</p>
                </div>
              </div>
              
              <div className="space-y-6 md:space-y-8">
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">所在地</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600 break-words">{companyInfo.address}</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">電話番号</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600">{companyInfo.phone}</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">FAX</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600">{companyInfo.fax}</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">営業時間</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600">{companyInfo.hours}</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-800 mb-3">定休日</h3>
                  <p className="text-base sm:text-lg md:text-xl text-gray-600">{companyInfo.closed}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>資格・許可・加盟団体</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              適切な許可と資格を取得し、関連団体に加盟して質の高いサービスを提供しています。
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {certifications.map((cert, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-5 md:p-6 flex items-center gap-4 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <i className="ri-check-line text-green-500 text-2xl md:text-3xl flex-shrink-0"></i>
                <span className="text-base sm:text-lg md:text-xl text-gray-700 break-words">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 md:mb-6">
              <strong>アクセス</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600">
              岡山市北区に位置し、アクセスしやすい立地です。
            </p>
          </div>
          
          <div className="bg-gray-100 rounded-xl shadow-xl overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3281.8!2d133.9341!3d34.6617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDM5JzQyLjEiTiAxMzPCsDU2JzAyLjgiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="おかやま放課後等デイサービス 所在地"
            ></iframe>
          </div>
          
          <div className="mt-8 md:mt-10 text-center">
            <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-8 md:p-10 shadow-lg">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-800 mb-6">交通アクセス</h3>
              <div className="space-y-3 text-base sm:text-lg md:text-xl text-gray-600">
                <p>JR岡山駅から車で約15分</p>
                <p>岡山ICから車で約20分</p>
                <p>駐車場完備（5台分）</p>
                <p>送迎サービスあり（岡山市内）</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
